import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NotificationOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Permissions from '../auth/Permissions';

import BreadCrumbContainer from '../common/breadcrumbContainer/breadcrumbContainer';
import CardGrid from '../common/cardGrid/cardGrid';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import FilterDropdown from '../common/FilterDropdown';
import OnTraccrButton from '../common/buttons/OnTraccrButton';

import ContactAddView from './ContactAddView';
import ContactEditView from './ContactEditView';
import ContactCard from './ContactCard';
import CustomerClientBroadcast from './customers/CustomerClientBroadcast';

import { filterOptions } from './contactFilters';

import { getGlobalAddressBook } from './state/contacts.actions';
import {
  createCustomer,
  getCustomers,
  archiveCustomer,
  bulkArchiveCustomers,
  updateCustomer,
  deleteCustomer,
  bulkDeleteCustomers,
  getCustomerLabels,
} from './customers/state/customers.actions';

import {
  getAllPhases,
} from '../costcodes/state/costcodes.actions';
import { getEquipment } from '../equipment/state/equipment.actions';
import {
  getProjects,
  getProjectEquipment,
} from '../projects/state/projects.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';
import { getTemplates } from '../forms/state/forms.actions';
import { getCardLinks } from '../boards/state/boards.actions';
import {
  getClientPortalAccounts,
  getClientPortalSettings,
} from '../clientPortal/state/clientPortal.actions';

import sortByString, { getIdMap, safeExtractFromObject } from '../helpers/helpers';
import {
  archiveVendor,
  bulkArchiveVendors,
  createVendor,
  deleteVendor,
  bulkDeleteVendors,
  getVendorLabels,
  getVendors,
  updateVendor,
} from './vendors/state/vendors.actions';
import { getLabels } from '../labels/state/labels.actions';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import { getDisplayColumns } from './contacts.constants';

const getCustomerCrumbs = (t) => [{
  text: t('Customer', { count: 2 }),
  icon: 'customers',
}];

const VENDOR_CRUMBS = [{
  text: 'Vendors',
  icon: 'vendors',
}];

export default function Contacts({
  history,
  isVendor,
}) {
  const { t } = useTranslation();
  const hasWritePerms = isVendor
    ? Permissions.has('VENDORS_WRITE') : Permissions.has('CUSTOMERS_WRITE');
  const hasReadPerms = isVendor
    ? Permissions.has('VENDORS_READ') : Permissions.has('CUSTOMERS_READ');
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    state: {
      targetId: locationStateId,
      activeTab,
    } = {},
    pathname,
  } = location;
  const cardGridRef = useRef(null);

  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const vendors = useSelector((state) => state.vendors.vendors);
  const vendorToLabel = useSelector((state) => state.vendors.vendorToLabel);
  const customers = useSelector((state) => state.customers.customers);
  const customerToLabel = useSelector((state) => state.customers.customerToLabel);
  const unreadClientComms = useSelector((state) => state.customers.unreadClientComms);
  const unreadVendorComms = useSelector((state) => state.vendors.unreadVendorComms);
  const {
    communicators = [],
  } = useSelector((state) => state.clientPortal.settings);
  const company = useSelector((state) => state.settings.company);
  const { userId: ownerId } = company ?? {};

  const labels = useSelector((state) => state.labels);
  const labelToCustomer = useSelector((state) => state.customers.labelToCustomer);
  const labelToVendor = useSelector((state) => state.vendors.labelToVendor);
  const divisionMap = useSelector((state) => state.settings.divisions);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const contactLabels = useMemo(() => (
    labels.filter((label) => label.type === (isVendor ? 'vendors' : 'customers'))
  ), [isVendor, labels]);

  const [activeFilters, setActiveFilters] = useState({
    active: new Set([1]),
    unread: new Set([0, 1]),
    labels: new Set(),
    type: new Set(['Subcontractor', 'Supplier']),
  });
  const [activeSort, setActiveSort] = useState('Name: Ascending');
  const [showBroadcastDrawer, setShowBroadcastDrawer] = useState(false);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);

  const hideBroadcastDrawer = useCallback(() => setShowBroadcastDrawer(false), []);
  const openBroadcastDrawer = useCallback(() => setShowBroadcastDrawer(true), []);
  const onFilter = useCallback((checkedFilters, key) => {
    const newFilters = new Set(checkedFilters);
    setActiveFilters({
      ...activeFilters,
      [key]: newFilters,
    });
  }, [activeFilters]);

  const validateCustomFields = useCallback((currentStep, values) => {
    if (currentStep !== 0) return;

    const payload = formatCustomFieldCreatePayload({
      payload: values,
      customFields,
    });

    if (payload?.error || !payload) {
      setErrors(payload?.errorMap);
      throw new Error('Validation error with custom fields');
    }

    setErrors({});
  }, [customFields]);

  const onAdd = useCallback((values) => {
    if (isVendor) return dispatch(createVendor(values));

    const payload = formatCustomFieldCreatePayload({
      payload: values,
      customFields,
    });

    if (payload?.error) {
      setErrors(payload?.errorMap);
      return;
    }

    setErrors({});

    return dispatch(createCustomer(payload));
  }, [customFields]);

  const onEdit = useCallback((id, data) => {
    if (isVendor) return dispatch(updateVendor(id, data, vendorToLabel));

    const payload = formatCustomFieldCreatePayload({
      payload: data,
      customFields,
    });

    if (payload?.error) {
      setErrors(payload?.errorMap);
      throw new Error('Validation error with custom fields');
    }

    setErrors({});

    return dispatch(updateCustomer(id, payload, customerToLabel));
  }, [
    vendorToLabel,
    customerToLabel,
    customFields,
  ]);

  const onArchive = useCallback((contact) => {
    const mode = contact.active ? 'Archive' : 'Activate';
    CustomConfirmModal({
      title: `${mode} ${isVendor ? 'Vendor' : t('Customer')} '${contact.name}'?`,
      okText: mode,
      cancelText: 'Cancel',
      onOk() {
        dispatch(isVendor
          ? archiveVendor(contact.id, !contact.active)
          : archiveCustomer(contact.id, !contact.active));
      },
    });
  }, [isVendor]);

  const onDelete = useCallback((contact) => {
    CustomConfirmModal({
      title: `Delete ${isVendor ? 'Vendor' : t('Customer')} '${contact.name}'?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk() {
        dispatch(isVendor
          ? deleteVendor(contact.id)
          : deleteCustomer(contact.id));
      },
    });
  }, [isVendor]);

  const entityLabel = isVendor ? 'vendors' : `${t('Customer').toLowerCase()}s`;

  const onBulkArchive = useCallback(async (ids, { password }) => (
    dispatch(isVendor
      ? bulkArchiveVendors(ids, { password }, false)
      : bulkArchiveCustomers(ids, { password }, false))
  ), [isVendor]);

  const onBulkActivate = useCallback(async (ids, { password }) => (
    dispatch(isVendor
      ? bulkArchiveVendors(ids, { password }, true)
      : bulkArchiveCustomers(ids, { password }, true))
  ), [isVendor]);

  const onBulkDelete = useCallback(async (ids, { password }) => (
    dispatch(isVendor
      ? bulkDeleteVendors(ids, { password })
      : bulkDeleteCustomers(ids, { password }))
  ), [isVendor]);

  useEffect(() => {
    dispatch(getLabels());
    dispatch(getVendors());
    dispatch(getVendorLabels());
    dispatch(getCustomers());
    dispatch(getCustomerLabels());
    dispatch(getProjects());
    dispatch(getAllPhases());
    dispatch(getProjectEquipment());
    dispatch(getEquipment());
    dispatch(getBillingRates());
    dispatch(getTemplates());
    dispatch(getCardLinks());
    dispatch(getClientPortalSettings());
    dispatch(getClientPortalAccounts());
    dispatch(getGlobalAddressBook());
  }, [dispatch]);

  const openItemFromLocationState = useCallback(() => {
    if (!locationStateId || !cardGridRef.current) {
      return;
    }

    if (isVendor) {
      const selectedVendor = vendors[locationStateId];
      if (selectedVendor) {
        cardGridRef.current.displayItem({
          ...selectedVendor,
          activeTab: activeTab || 'generalInfo',
        });
      }
    } else {
      const selectedCustomer = customers[locationStateId];
      if (selectedCustomer) {
        cardGridRef.current.displayItem({
          ...selectedCustomer,
          activeTab: activeTab || 'generalInfo',
        });
      }
    }
  }, [locationStateId, cardGridRef, vendors, customers, isVendor, activeTab]);

  useEffect(() => {
    if (locationStateId && cardGridRef.current && ((isVendor && Object.keys(vendors).length > 0) || (!isVendor && Object.keys(customers).length > 0))) {
      setTimeout(() => {
        openItemFromLocationState();

        window.history.replaceState({}, '', pathname);
      }, 250);
    }
  }, [locationStateId, cardGridRef, vendors, customers, isVendor, activeTab, pathname, openItemFromLocationState]);

  useEffect(() => {
    if (locationStateId && cardGridRef.current && ((isVendor && Object.keys(vendors).length > 0) || (!isVendor && Object.keys(customers).length > 0))) {

      const timer = setTimeout(() => {
        openItemFromLocationState();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    const cachedSort = window.localStorage.getItem('contactsSort') || 'Name: Ascending';
    setActiveSort(cachedSort);
  }, []);

  const unreadCountMap = useMemo(() => {
    const map = {};
    const contactId = isVendor ? 'vendorId' : 'customerId';
    (isVendor ? unreadVendorComms : unreadClientComms).forEach((note) => {
      const {
        [note[contactId]]: oldValue = 0,
      } = map;
      map[note[contactId]] = oldValue + 1;
    });
    return map;
  }, [isVendor, unreadClientComms, unreadVendorComms]);
  const contactList = useMemo(() => {
    const {
      labels: activeLabels = new Set(),
      type,
    } = activeFilters;
    const labelToContact = isVendor ? labelToVendor : labelToCustomer;

    const contactsFromLabels = new Set();
    if (activeLabels.size > 0) {
      Array.from(activeLabels).forEach((label) => {
        const {
          [label]: labelContactIds = [],
        } = labelToContact;
        labelContactIds.forEach((contactId) => {
          contactsFromLabels.add(contactId);
        });
      });
    }

    const list = Object.values(isVendor ? vendors : customers)
      .filter((contact) => {
        if (!activeFilters.active.has(contact.active)) return false;
        if (isVendor && !type.has(contact.type)) return false;
        if (!contact?.divisionIds?.find((divisionId) => selectedDivisions.has(divisionId))) return false;
        const { id: contactId } = contact;
        const { [contactId]: unreadCount = 0 } = unreadCountMap;
        const { unread: unreadFilter = new Set() } = activeFilters;

        const unreadMatch = (
          (unreadCount > 0 && unreadFilter.has(1))
          || (unreadCount === 0 && unreadFilter.has(0))
        );

        // If labels filter is empty we show everyone
        const labelMatch = activeLabels.size === 0 || contactsFromLabels.has(contactId);
        return unreadMatch && labelMatch;
      });
    const mod = activeSort === 'Name: Ascending' ? 1 : -1;
    list.sort((a, b) => sortByString('name')(a, b) * mod);
    return list;
  }, [
    isVendor,
    customers,
    vendors,
    activeFilters,
    activeSort,
    unreadCountMap,
    labelToVendor,
    labelToCustomer,
    selectedDivisions,
  ]);

  const filterIsAtDefault = useMemo(() => {
    const { active, unread, labels } = activeFilters;
    return active.has(1) && !active.has(0) && unread.has(0) && unread.has(1) && labels.size === 0;
  }, [activeFilters]);

  const sortData = useMemo(() => ({
    sortOptions: [
      {
        title: 'Name: Ascending',
        icon: 'sort-ascending',
        action: () => {
          window.localStorage.setItem('contactsSort', 'Name: Ascending');
          setActiveSort('Name: Ascending');
        },
      }, {
        title: 'Name: Descending',
        icon: 'sort-descending',
        action: () => {
          window.localStorage.setItem('contactsSort', 'Name: Descending');
          setActiveSort('Name: Descending');
        },
      },
    ],
    sortActive: activeSort,
  }), [activeSort]);

  const canSend = useMemo(() => (
    Permissions.id === ownerId || communicators.includes(Permissions.id)
  ), [communicators, ownerId]);

  const CUSTOMER_CRUMBS = getCustomerCrumbs(t);

  const AddView = useCallback((form, formProps) => (
    <ContactAddView
      form={form}
      isVendor={isVendor}
      customFields={customFields}
      setCustomFields={setCustomFields}
      errors={errors}
      {...formProps}
    />
  ), [isVendor, customFields, errors]);

  const EditView = useCallback((form, formProps) => (
    <ContactEditView
      form={form}
      isVendor={isVendor}
      customFields={customFields}
      setCustomFields={setCustomFields}
      errors={errors}
      {...formProps}
    />
  ), [isVendor, customFields, errors]);

  const labelMap = useMemo(() => getIdMap(contactLabels), [contactLabels]);

  const displayColumns = useMemo(() => (
    getDisplayColumns(
      divisionMap,
      settings?.uxRedesignPt1,
      labelMap,
      isVendor ? vendorToLabel : customerToLabel,
    )
  ), [
    divisionMap,
    settings,
    isVendor,
    contactLabels,
    vendorToLabel,
    customerToLabel,
  ]);

  if (!hasReadPerms) {
    history.replace('/dashboard');
    return null;
  }

  return (
    <>
      <BreadCrumbContainer crumbs={isVendor ? VENDOR_CRUMBS : CUSTOMER_CRUMBS}>
        <CardGrid
          ref={cardGridRef}
          dataSource={contactList}
          search={{
            customRanking: (item, searchTerm) => {
              const concatenatedString = [item.name, item.officeEmail, item.officeNumber]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return concatenatedString.indexOf(searchTerm);
            },
          }}
          itemView={ContactCard}
          displayColumns={displayColumns}
          isListView
          itemDimensions={{ width: 239, height: 190 }}
          add={hasWritePerms ? {
            title: `Enter ${isVendor ? 'Vendor' : t('Customer')} Details`,
            formView: AddView,
            width: 1000,
            onClose: onAdd,
            numberOfSteps: 2,
            customValidator: validateCustomFields,
          } : null}
          edit={{
            zIndex: 890,
            title: `Enter ${isVendor ? 'Vendor' : t('Customer')} Details`,
            formView: EditView,
            width: 1000,
            onClose: onEdit,
            numberOfSteps: 2,
            canEdit: () => hasWritePerms,
            canArchive: () => hasWritePerms,
            canDelete: () => hasWritePerms,
            customValidator: validateCustomFields,
          }}
          onArchive={onArchive}
          onDelete={onDelete}
          bulkActions={hasWritePerms ? {
            canArchive: true,
            canActivate: true,
            canDelete: true,
            entityLabel,
          } : null}
          onBulkArchive={onBulkArchive}
          onBulkActivate={onBulkActivate}
          onBulkDelete={onBulkDelete}
          filter={(
            <FilterDropdown
              filters={[filterOptions({ labels: contactLabels, isVendor })]}
              activeFilters={activeFilters}
              onFilter={onFilter}
              useRedesign={settings?.uxRedesignPt1}
            />
          )}
          filterActive={filterIsAtDefault}
          sort={sortData}
          extra={
            canSend && !isVendor && (
            <OnTraccrButton
              type="cancel"
              title="Message"
              icon={<NotificationOutlined />}
              onClick={openBroadcastDrawer}
            />
            )
          }
          title={isVendor ? 'Vendors' : t('Customers')}
          entity={isVendor ? 'Vendor' : t('Customer')}
        />
      </BreadCrumbContainer>
      <CustomerClientBroadcast
        visible={showBroadcastDrawer}
        onClose={hideBroadcastDrawer}
      />
    </>
  );
}

Contacts.propTypes = {
  isVendor: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

